import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Image, Spin } from 'antd'
import { api } from '../api'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import { ChatMarkdown } from './ChatMarkdown'
import type { DocFigureRef } from './ChatSourceFigures'

const SEE_BELOW_RE = /见下图|如下图所示|如图所示|见图|配图/
const FIG_NUM_RE = /图\s*0*(\d+)/g

export type AnswerFigureRef = DocFigureRef & {
  item_id: number
  scope?: KnowledgeScope
}

type Segment = {
  text: string
  figureIndexes: number[]
}

function figureIndexByNumber(figures: AnswerFigureRef[], num: string): number | null {
  const normalized = num.replace(/^0+/, '') || num
  const padded = normalized.padStart(3, '0')
  const idx = figures.findIndex((f) => {
    const fk = (f.figure_key || '').replace(/^0+/, '') || f.figure_key
    return fk === normalized || f.figure_key === padded || f.label === `图${normalized}`
  })
  return idx >= 0 ? idx : null
}

function splitAnswerSegments(content: string, figures: AnswerFigureRef[]): Segment[] {
  const text = (content || '').trim()
  if (!text) return []
  if (!figures.length) return [{ text, figureIndexes: [] }]

  const blocks = text.split(/\n\n+/).map((b) => b.trim()).filter(Boolean)
  const segments: Segment[] = []
  const usedFigureIdx = new Set<number>()
  let fallbackUsed = false

  for (const block of blocks) {
    const indexes: number[] = []
    FIG_NUM_RE.lastIndex = 0
    let m: RegExpExecArray | null
    while ((m = FIG_NUM_RE.exec(block)) !== null) {
      const idx = figureIndexByNumber(figures, m[1])
      if (idx != null && !usedFigureIdx.has(idx)) {
        indexes.push(idx)
        usedFigureIdx.add(idx)
      }
    }
    if (!indexes.length && !fallbackUsed && SEE_BELOW_RE.test(block)) {
      const firstFree = figures.findIndex((_, i) => !usedFigureIdx.has(i))
      if (firstFree >= 0) {
        indexes.push(firstFree)
        usedFigureIdx.add(firstFree)
        fallbackUsed = true
      }
    }
    segments.push({ text: block, figureIndexes: indexes })
  }

  return segments.length ? segments : [{ text, figureIndexes: [] }]
}

async function fetchChatFigureBlob(
  itemId: number,
  storedName: string,
  scope: KnowledgeScope,
): Promise<string | null> {
  try {
    const res = await api.get(
      `/public/chat/figures/${itemId}/${encodeURIComponent(storedName)}`,
      { params: { scope }, responseType: 'blob' },
    )
    const blob = res.data as Blob
    return URL.createObjectURL(blob)
  } catch {
    return null
  }
}

type Props = {
  content: string
  figures: AnswerFigureRef[]
  defaultScope: KnowledgeScope
}

export function ChatAnswerFigures({ content, figures, defaultScope }: Props) {
  const segments = useMemo(() => splitAnswerSegments(content, figures), [content, figures])
  const [urls, setUrls] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const blobUrlsRef = useRef<string[]>([])

  useEffect(() => {
    if (!figures.length) {
      setUrls({})
      return
    }
    let cancelled = false
    setLoading(true)
    Promise.all(
      figures.map(async (fig) => {
        const stored = fig.stored_name
        if (!stored) return null
        const scope =
          fig.scope === 'personal' || fig.scope === 'enterprise' ? fig.scope : defaultScope
        const url = await fetchChatFigureBlob(fig.item_id, stored, scope)
        if (!url) return null
        blobUrlsRef.current.push(url)
        return [`${fig.item_id}:${stored}`, url] as const
      }),
    )
      .then((pairs) => {
        if (cancelled) return
        const next: Record<string, string> = {}
        for (const p of pairs) {
          if (p) next[p[0]] = p[1]
        }
        setUrls(next)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
      for (const u of blobUrlsRef.current) URL.revokeObjectURL(u)
      blobUrlsRef.current = []
    }
  }, [figures, defaultScope])

  if (!content.trim() && !figures.length) return null

  const urlKey = (fig: AnswerFigureRef) => `${fig.item_id}:${fig.stored_name}`

  return (
    <div className="chat-msg-answer-body">
      {segments.map((seg, segIdx) => (
        <Fragment key={segIdx}>
          {seg.text ? <ChatMarkdown content={seg.text} /> : null}
          {seg.figureIndexes.map((figIdx) => {
            const fig = figures[figIdx]
            if (!fig) return null
            const src = urls[urlKey(fig)]
            return (
              <figure key={`${figIdx}-${fig.stored_name}`} className="chat-msg-answer-figure">
                {loading && !src ? (
                  <Spin size="small" />
                ) : src ? (
                  <Image
                    src={src}
                    alt={fig.label || fig.figure_key}
                    className="chat-msg-answer-figure-img"
                  />
                ) : null}
                {fig.label ? <figcaption>{fig.label}</figcaption> : null}
              </figure>
            )
          })}
        </Fragment>
      ))}
    </div>
  )
}
